import React from 'react';
import { Box, Alert } from 'pounceV2';
import { DEFAULT_LARGE_PAGE_SIZE } from 'Source/constants';
import useInfiniteScroll from 'Hooks/useInfiniteScroll';
import TablePlaceholderV3 from 'Components/TablePlaceholder/TablePlaceholderV3';
import { extractErrorMessage } from 'Helpers/utils';
import ErrorBoundaryV3 from 'Components/ErrorBoundary/ErrorBoundaryV3';
import { useViewBucketData } from 'Pages/Configure/LogSources/SchemasManagement/graphql/ViewBucketData.generated';
import { LogStreamType } from 'Generated/schema';
import ViewBucketDataRawEventsV3 from './ViewBucketDataRawEventsV3';

export type ViewBucketDataSideSheetContentProps = {
  prefix: string;
  streamType: LogStreamType;
  sourceId: string;
  show: boolean;
};

const ViewBucketDataSideSheetContentV3 = ({
  prefix,
  streamType,
  sourceId,
  show,
}: ViewBucketDataSideSheetContentProps) => {
  const {
    loading,
    previousData,
    data = previousData,
    error,
    fetchMore,
  } = useViewBucketData({
    fetchPolicy: 'network-only',
    skip: !show,
    variables: {
      input: {
        sourceId,
        filters: { prefix },
        streamType,
        pageSize: DEFAULT_LARGE_PAGE_SIZE,
      },
    },
  });

  const eventList = data?.viewBucketData?.data ?? [];
  const hasNextPage = data?.viewBucketData?.pageInfo.hasNextPage;
  const paginationToken = data?.viewBucketData?.pageInfo.paginationToken;
  const hasError = Boolean(error);
  const { sentinelRef } = useInfiniteScroll<HTMLDivElement>({
    loading,
    threshold: 500,
    onLoadMore: () => {
      fetchMore({
        variables: {
          input: {
            sourceId,
            filters: { prefix },
            streamType,
            pageSize: DEFAULT_LARGE_PAGE_SIZE,
            paginationToken,
          },
        },
      });
    },
  });

  if (loading) {
    return (
      <Box p={5}>
        <TablePlaceholderV3 rowCount={5} rowHeight={30} />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box p={5}>
        <Alert
          variant="error"
          title="Couldn't load your events"
          description={
            extractErrorMessage(error) ||
            'There was an error when performing your request, please contact support@runpanther.io'
          }
        />
      </Box>
    );
  }

  return (
    <ErrorBoundaryV3>
      {loading && !data ? (
        <Box p={5}>
          <TablePlaceholderV3 rowCount={5} rowHeight={30} />
        </Box>
      ) : (
        <ViewBucketDataRawEventsV3 events={eventList} withS3Keys={prefix.endsWith('/')} />
      )}
      {hasNextPage && (
        <Box p={5} ref={sentinelRef}>
          <TablePlaceholderV3 rowCount={5} rowHeight={30} />
        </Box>
      )}
    </ErrorBoundaryV3>
  );
};

export default ViewBucketDataSideSheetContentV3;
